<template>
  <Card :variant="cardVariant" size="medium" :class="['file-card', { ghost: variant === 'ghost' }]">
    <View gap="sm">
      <FileThumbnail :thumbnail-url="thumbnailUrl" />

      <View :grow="1" direction="column">
        <Text variant="label-medium">{{ fileName }}</Text>

        <Text variant="body-medium" color="secondary" v-if="fileSize && !hasProgress">
          {{ getFormattedFileSize(fileSize) }}
        </Text>

        <Text variant="body-medium" color="secondary" v-if="hasProgress">{{ progressText }}</Text>
      </View>

      <IconButton v-if="isRemovable" class="remove-icon" icon="trash" size="medium" variant="ghost" @click="remove" />
    </View>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { Card, IconButton, Text, View } from '@tallo/design-system';

import { FileListItemVariant } from '../interfaces/file-list-item-variant';
import { getFormattedFileSize } from '../utils/formatted-file-size.util';
import FileThumbnail from './file-thumbnail.vue';

const props = withDefaults(
  defineProps<{
    fileName: string;
    fileSize?: number;
    uploaded?: number;
    thumbnailUrl?: string | null;
    isRemovable?: boolean;
    variant?: FileListItemVariant;
  }>(),
  {
    variant: 'outline',
  },
);
const emit = defineEmits<{ remove: [] }>();

const cardVariant = computed(() => (props.variant === 'ghost' ? 'primary' : 'outline'));
const progress = computed(() => {
  if (props.fileSize !== undefined && props.uploaded !== undefined) {
    return Math.round((props.uploaded / props.fileSize) * 100);
  }

  return undefined;
});
const progressText = computed(() => {
  switch (progress.value) {
    case 0:
      return 'Pending upload';

    case 100:
      return 'Processing file';

    default:
      return `${progress.value}% uploaded`;
  }
});
const hasProgress = computed(() => progress.value !== undefined);

function remove(event: Event) {
  event.stopPropagation();
  emit('remove');
}
</script>

<style scoped lang="scss">
.file-card.ghost {
  padding: 0;
}

.file-icon {
  width: 2.5rem;
  height: 2.5rem;
  background-color: var(--t-color-neutral-200);
  border-radius: var(--t-border-radius-sm);
  color: var(--t-color-neutral-500);
  overflow: hidden;
  flex-shrink: 0;

  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
}

.file-icon,
.remove-icon {
  flex-shrink: 0;
}
</style>
